import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { MFChannel } from '../../Components/SDK';
import {
   getActiveChannelId,
   getSelectedChannels,
   getSelectedMessages,
   getVirtualizedListRef,
   resetSelectedMessages,
   setActiveConversationChannel,
   updateDeleteMessages,
} from '../../Slice';
import Store from '../../Store';
import RecentChannelList from '../pages/RecentChannels';
import { CloseWhite, Delete, SelectedForward } from '../rootAccess';
import { resetActionModal, setActionModal } from './actionModalUtils';
import { resetAlertModal, setAlertModal } from './alertModalUtils';
import { clearChannelSearchData } from './channelSearchUtils';
import { getLocalUserDetails } from './commonActions';
import { scrollToBottomWithRetry } from './conversationActions';
import { showErrorToast } from './toast';

export const resetConversationState = channelId => {
   Store.dispatch(resetSelectedMessages({ channelId }));
};

// Check whether all selected messages are within the last 10 minutes
export const isAnyMessageWithinLast10Minutes = (messages = []) => {
   const now = Date.now();
   const { userId } = getLocalUserDetails() || {};
   const TEN_MINUTES_MS = 10 * 60 * 1000; // 600000 ms
   return messages.every(
      message => now - message.messageTime <= TEN_MINUTES_MS && message.senderId === userId && !message?.isDeletedForEveryOne,
   );
};

export const handleDeletForEveryOne = async (activeChannelId, messageIds) => {
   await MFChannel.deleteMessageForEveryOne({ channelId: activeChannelId, messageIds });
   Store.dispatch(updateDeleteMessages({ channelId: activeChannelId, messageIds, isDeleteForAll: true }));
   Store.dispatch(resetSelectedMessages({ channelId: activeChannelId }));
};

export const handleDeletForme = async (activeChannelId, messageIds) => {
   await MFChannel.deleteMessageForMe({ channelId: activeChannelId, messageIds });
   Store.dispatch(updateDeleteMessages({ channelId: activeChannelId, messageIds, isDeleteForAll: false }));
   Store.dispatch(resetSelectedMessages({ channelId: activeChannelId }));
};

export const messageActionOptions = {
   forward: {
      icon: <SelectedForward />,
      formatter: () => {
         setActionModal({
            visible: true,
            headerIconLeft: <CloseWhite onClick={resetActionModal} />,
            header: 'Forward To',
            // body: (
            //    <RecentChannelList
            //       component={'forward'}
            //       isInModal={true}
            //       isSelectable
            //       maxSelectionCount={5}
            //       scopeKey="modal"
            //    />
            // ),
            // footer: <ForwardFooter />,
         });
      },
   },
   delete: {
      icon: <Delete />,
      formatter: () => {
         const activeChannelId = getActiveChannelId();
         const selectedMessages = getSelectedMessages(activeChannelId);
         const deleteForEveryOne = isAnyMessageWithinLast10Minutes(selectedMessages);
         const multiple = selectedMessages.length > 1;
         const messageIds = selectedMessages.map(message => message.messageId);
         clearChannelSearchData(activeChannelId);
         setAlertModal({
            visible: true,
            title: `Are you sure you want to delete selected message${multiple ? 's' : ''}?`,
            yesButton: deleteForEveryOne ? 'Delete for everyone' : 'Delete for me',
            yesAction: () => {
               deleteForEveryOne
                  ? handleDeletForEveryOne(activeChannelId, messageIds)
                  : handleDeletForme(activeChannelId, messageIds);
            },
            noButton: 'Cancel',
            noAction: () => {},
            optionalButton: deleteForEveryOne ? 'Delete for me' : undefined,
            optionalAction: deleteForEveryOne
               ? () => {
                    resetAlertModal();
                    handleDeletForme(activeChannelId, messageIds);
                 }
               : undefined,
            onRequestClose: () => resetAlertModal(),
         });
      },
   },
};

export const handleForwardMessage = async () => {
   try {
      const activeChannelId = getActiveChannelId();
      const selectedChannels = getSelectedChannels();
      const selectedMessages = getSelectedMessages(activeChannelId);
      const localUserDetails = getLocalUserDetails();
      const selectedMessagesCount = selectedMessages.length;
      const deleteMessagesCount = selectedMessages.filter(
         message => message.isDeletedForEveryone || message.isDeletedForMe,
      ).length;
      if (selectedMessagesCount === deleteMessagesCount) {
         showErrorToast('All selected messages are deleted');
         return;
      }

      if (deleteMessagesCount > 0) {
         const deletedCountLabel = deleteMessagesCount === 1 ? 'message' : 'messages';
         showErrorToast(
            `${deleteMessagesCount} ${deletedCountLabel} can't be forwarded, because the original ${deletedCountLabel} ${
               deleteMessagesCount === 1 ? 'is' : 'are'
            } deleted`,
         );
      }
      const selectedChannelArr = Object.values(selectedChannels);
      if (selectedChannelArr.length === 1) {
         Store.dispatch(setActiveConversationChannel(selectedChannelArr[0]));
      }

      const channelMessageMappings = [];

      selectedChannelArr.forEach(channel => {
         // Generate newMessageId for each message per channel
         const messagesWithNewIds = selectedMessages.map(message => ({
            ...message,
            newMessageId: `MSG-${uuidv4()}`,
         }));

         const transformedMessages = messagesWithNewIds.map(message => {
            const currentTime = Date.now();
            const currentTimeISO = new Date(currentTime).toISOString();

            return {
               messageId: message.newMessageId, // Use the channel-specific newMessageId
               senderId: localUserDetails?.fromUser || localUserDetails?.userId, // Updated with logged in user details
               receiverId: '',
               chatType: 'channel',
               messageType: message.messageType,
               content: message.content, // Keep original content structure
               reactions: null,
               replyTo: '',
               messageTime: currentTime, // Updated with current time
               IsForward: true,
               createdAt: currentTimeISO, // Updated with current time
               editedAt: null,
               updatedAt: currentTimeISO, // Updated with current time
               isDeletedForMe: false, // Keep this
               isDeletedForEveryone: false, // Keep this
               meta: null,
               channelId: channel.channelId, // Updated with channel.channelId
            };
         });

         // Store channel-specific message mappings
         channelMessageMappings.push({
            channelId: channel.channelId,
            messages: messagesWithNewIds.map(message => ({
               originalChannelId: activeChannelId,
               originalMessageId: message.messageId,
               newMessageId: message.newMessageId, // Use the channel-specific newMessageId
            })),
         });

         // Scroll to bottom for the target channel after forwarding
         const virtualizedListRef = getVirtualizedListRef(channel.channelId)();
         if (virtualizedListRef?.jumpToBottom) {
            // Use virtualized list's jumpToBottom
            setTimeout(() => {
               virtualizedListRef.jumpToBottom({ smooth: true });
            }, 200);
         } else if (virtualizedListRef?.getScrollContainer) {
            // Fallback: use getScrollContainer if available
            const scrollElement = virtualizedListRef.getScrollContainer();
            if (scrollElement) {
               const hasMediaMessages = transformedMessages.some(msg =>
                  ['image', 'video', 'audio', 'document'].includes(msg.messageType),
               );
               const messageType = hasMediaMessages ? 'image' : 'text';

               scrollToBottomWithRetry(scrollElement, {
                  messageType,
                  delay: 200,
                  maxRetries: 8,
               });
            }
         }
      });

      // Use the channel-specific message mappings
      await MFChannel.forwardMessages(channelMessageMappings);

      resetActionModal();
      Store.dispatch(resetSelectedMessages({ channelId: activeChannelId }));
   } catch (error) {
      console.error('Failed to handle forward message', error);
   }
};
